import { useEffect, useId, useState } from 'react';
import { Box, Collapse, List, ListItemButton, ListItemIcon, ListItemText } from '@mui/material';
import { alpha } from '@mui/material/styles';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ROUTES } from '@/constants/route-config';
import { darkTokens } from '@/theme/tokens';
import { extractLocaleFromPath, getLocalizedPath } from '@/utils/locale';

export function AboutNavigation({ onNavigate }: { onNavigate: () => void }) {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const { path, locale } = extractLocaleFromPath(pathname);
  const isAboutPage = path === ROUTES.PRIVACY_POLICY || path === ROUTES.TERMS_OF_SERVICE;
  const [expanded, setExpanded] = useState(isAboutPage);
  const contentId = useId();

  useEffect(() => {
    if (isAboutPage) setExpanded(true);
  }, [isAboutPage, path]);

  const rowSx = {
    borderRadius: 1,
    minHeight: 44,
    px: 1.25,
    py: 1,
    color: darkTokens.textSecondary,
    '&:hover': { bgcolor: 'rgba(255,255,255,0.04)' },
    '&.Mui-focusVisible': { outline: '2px solid #99CAFF', outlineOffset: -2 },
  };
  const iconSx = { minWidth: 0, mr: 1.25, color: 'inherit', '& svg': { fontSize: 15 } };
  const items = [
    { path: ROUTES.PRIVACY_POLICY, label: t('nav.privacy-policy', { defaultValue: 'Privacy Policy' }), icon: <LockOutlinedIcon /> },
    { path: ROUTES.TERMS_OF_SERVICE, label: t('nav.terms-of-service', { defaultValue: 'Terms of Service' }), icon: <DescriptionOutlinedIcon /> },
  ];

  return (
    <Box sx={{ borderTop: `1px solid ${darkTokens.border}`, px: 1.25, py: 1, flexShrink: 0 }}>
      <ListItemButton
        component="a"
        href="https://insight.deview.ai/"
        target="_blank"
        rel="noopener noreferrer"
        onClick={onNavigate}
        sx={{ ...rowSx, color: darkTokens.accent, '&:hover': { bgcolor: alpha(darkTokens.accent, 0.08) } }}
      >
        <ListItemIcon sx={iconSx}><OpenInNewIcon /></ListItemIcon>
        <ListItemText primary="Deview Insight" primaryTypographyProps={{ fontSize: 13, fontWeight: 600 }} />
      </ListItemButton>
      <ListItemButton
        component="button"
        type="button"
        aria-expanded={expanded}
        aria-controls={contentId}
        onClick={() => setExpanded((value) => !value)}
        sx={{ ...rowSx, width: '100%' }}
      >
        <ListItemIcon sx={iconSx}><InfoOutlinedIcon /></ListItemIcon>
        <ListItemText primary={t('nav.about-us', { defaultValue: 'About Us' })} primaryTypographyProps={{ fontSize: 12.5, fontWeight: 500 }} />
        <ChevronRightIcon sx={{ fontSize: 16, transform: expanded ? 'rotate(90deg)' : 'none' }} />
      </ListItemButton>
      <Collapse in={expanded} id={contentId}>
        <List disablePadding sx={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
          {items.map((item) => {
            const active = path === item.path;
            return (
              <Box component="li" key={item.path} sx={{ listStyle: 'none' }}>
                <ListItemButton
                  component={Link}
                  to={getLocalizedPath(item.path, locale ?? undefined)}
                  onClick={onNavigate}
                  aria-current={active ? 'page' : undefined}
                  sx={{
                    ...rowSx,
                    color: active ? darkTokens.accent : darkTokens.textSecondary,
                    bgcolor: active ? alpha(darkTokens.accent, 0.13) : 'transparent',
                    '&:hover': { bgcolor: active ? alpha(darkTokens.accent, 0.16) : 'rgba(255,255,255,0.04)' },
                  }}
                >
                  <ListItemIcon sx={iconSx}>{item.icon}</ListItemIcon>
                  <ListItemText primary={item.label} primaryTypographyProps={{ fontSize: 12.5, fontWeight: active ? 600 : 500 }} />
                </ListItemButton>
              </Box>
            );
          })}
        </List>
      </Collapse>
    </Box>
  );
}
